/**
 * The audit trail.
 *
 * Expandable rows carry the diff, because the whole value of the trail is
 * "what is different" and a table cell cannot hold `{field: {from, to}}`. The
 * row says who, what and when; the expansion says exactly what changed.
 *
 * Newest first, always, unless somebody sorts it otherwise — a trail read
 * oldest-first shows the beginning of time to somebody asking what just
 * happened.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity, UserRound } from 'lucide-react';
import {
  Alert,
  api,
  createFacetsFetcher,
  createServerFetcher,
  formatDateTime,
  formatRelative,
  humanize,
  PageHeader,
  ScrollArea,
  ServerTable,
  StatusChip,
  type ServerColumn,
  type Tone,
} from '@connect/web-shared';

interface AuditRow {
  public_id: string;
  action: string;
  resource: string;
  record_id: string | null;
  record_label: string | null;
  actor_name: string;
  changes: Record<string, { from: unknown; to: unknown }> | null;
  context: Record<string, unknown> | null;
  ip_address: string | null;
  request_id: string | null;
  created_at: string;
}

interface AuditOptions {
  resources: { value: string; label: string }[];
  actions: { value: string; label: string }[];
}

const ACTION_TONES: Record<string, Tone> = {
  create: 'success',
  update: 'info',
  delete: 'danger',
  view: 'neutral',
  enabled: 'success',
  disabled: 'warning',
};

const ENDPOINT = '/audit';

/** A JSON value as something readable in a cell. */
function present(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function AuditLogPage() {
  const options = useQuery({
    queryKey: ['audit', 'options'],
    queryFn: () => api.get<AuditOptions>(`${ENDPOINT}/options`),
    staleTime: 10 * 60_000,
  });

  // The shared fetchers build the params, call the endpoint and unwrap the
  // page, so `toListParams` stays the one place the wire format is written.
  const fetchRows = useMemo(() => createServerFetcher<AuditRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const columns = useMemo<ServerColumn<AuditRow>[]>(
    () => [
      {
        field: 'created_at',
        header: 'When',
        icon: <Activity className="size-3" />,
        filter: 'date',
        width: 170,
        render: (row) => (
          <span title={formatDateTime(row.created_at)} className="whitespace-nowrap">
            {formatRelative(row.created_at)}
          </span>
        ),
      },
      {
        field: 'actor_name',
        header: 'Who',
        icon: <UserRound className="size-3" />,
        filter: 'text',
        width: 160,
        render: (row) => (
          <span className={row.actor_name === 'system' ? 'text-faint italic' : undefined}>
            {row.actor_name}
          </span>
        ),
      },
      {
        field: 'action',
        header: 'Action',
        filter: 'select',
        options: options.data?.actions,
        width: 110,
        render: (row) => <StatusChip value={row.action} tones={ACTION_TONES} />,
      },
      {
        field: 'resource',
        header: 'Resource',
        filter: 'select',
        options: options.data?.resources,
        width: 160,
        render: (row) => (
          <span className="font-mono text-2xs text-muted">{row.resource}</span>
        ),
      },
      {
        field: 'record_label',
        header: 'Record',
        filter: 'text',
        minWidth: 180,
        render: (row) => row.record_label ?? <span className="text-faint">—</span>,
      },
      {
        field: 'ip_address',
        header: 'From',
        filter: 'text',
        width: 130,
        hiddenByDefault: true,
      },
      {
        field: 'request_id',
        header: 'Request',
        filter: 'text',
        width: 150,
        hiddenByDefault: true,
        render: (row) => (
          <span className="font-mono text-2xs text-faint">{row.request_id ?? '—'}</span>
        ),
      },
    ],
    [options.data],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Audit log"
        description="Who changed what, when, and from what to what."
        breadcrumbs={[{ label: 'Access' }, { label: 'Audit log' }]}
      />

      <Alert tone="info" title="The trail is append-only">
        Each entry is written in the same transaction as the change it records, so a change and its
        entry cannot diverge. Nothing in Connect can edit or delete one.
      </Alert>

      <ServerTable<AuditRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['audit']}
        stateKey="audit.log"
        searchPlaceholder="Search by person or record"
        // The diff is the point of the trail and cannot fit in a cell.
        renderRowDetail={(row) => (
          <div className="space-y-3">
            {row.changes && Object.keys(row.changes).length > 0 ? (
              <div className="space-y-1.5">
                <p className="text-2xs font-medium uppercase tracking-wide text-faint">
                  Changed
                </p>
                <ScrollArea className="">
                  <table className="text-xs">
                    <thead>
                      <tr className="text-faint">
                        <th className="pr-4 text-left font-medium">Field</th>
                        <th className="pr-4 text-left font-medium">From</th>
                        <th className="text-left font-medium">To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(row.changes).map(([field, change]) => (
                        <tr key={field}>
                          <td className="pr-4 py-0.5 font-mono text-2xs text-muted">{field}</td>
                          <td className="pr-4 py-0.5 text-danger line-through">
                            {present(change.from)}
                          </td>
                          <td className="py-0.5 text-success">{present(change.to)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollArea>
              </div>
            ) : (
              <p className="text-xs text-faint">
                No field-level diff — this action created or removed a record rather than editing
                one.
              </p>
            )}

            {row.context && Object.keys(row.context).length > 0 ? (
              <div className="space-y-1">
                <p className="text-2xs font-medium uppercase tracking-wide text-faint">Context</p>
                <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-4">
                  {Object.entries(row.context).map(([key, value]) => (
                    <div key={key} className="min-w-0">
                      <dt className="text-2xs text-faint">{humanize(key)}</dt>
                      <dd className="truncate text-body">{present(value)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ) : null}

            <p className="text-2xs text-faint">
              {formatDateTime(row.created_at)}
              {row.record_id ? ` · record ${row.record_id}` : ''}
              {row.request_id ? ` · request ${row.request_id}` : ''}
            </p>
          </div>
        )}
        emptyTitle="Nothing recorded yet"
        emptyDescription="Entries appear here as soon as somebody changes something. A fresh installation has none."
      />
    </div>
  );
}
